import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusCircle, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';

const TIER_OPTIONS = [
  { key: 'rules',  label: 'Rules (free, deterministic)' },
  { key: 'gemma',  label: 'Gemma (cheap LLM)' },
  { key: 'claude', label: 'Claude (high-accuracy LLM)' },
];

function CampaignBuilder({ departments = [] }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [deptCode, setDeptCode] = useState('');
  const [minLineTotal, setMinLineTotal] = useState(1000);
  const [maxItems, setMaxItems] = useState(1000);
  const [tiers, setTiers] = useState({ rules: true, gemma: true, claude: false });
  const [success, setSuccess] = useState(null);

  const create = useMutation({
    mutationFn: (body) => apiClient.post('/api/backlog-buster/campaigns', body),
    onSuccess: (data) => {
      logger.ui.info?.('BacklogBuster', 'campaign_created', { id: data?.id });
      queryClient.invalidateQueries({ queryKey: ['backlog-buster', 'campaigns'] });
      setSuccess(data?.id || data?.campaign?.id || 'created');
      setName('');
    },
    onError: (err) => {
      logger.ui.error?.('BacklogBuster', 'campaign_create_failed', { message: err?.message });
    },
  });

  const toggleTier = (key) =>
    setTiers((t) => ({ ...t, [key]: !t[key] }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSuccess(null);
    const selectedTiers = Object.keys(tiers).filter((k) => tiers[k]);
    if (!name.trim()) return;
    if (selectedTiers.length === 0) return;

    const body = {
      name: name.trim(),
      filters: {
        dept_code: deptCode || undefined,
        min_line_total: Number(minLineTotal) || 0,
        max_items: Number(maxItems) || undefined,
      },
      dept_code: deptCode || undefined,
      min_line_total: Number(minLineTotal) || 0,
      max_items: Number(maxItems) || undefined,
      classification_tiers: selectedTiers,
    };
    create.mutate(body);
  };

  const tiersValid = Object.values(tiers).some(Boolean);
  const disabled = create.isPending || !name.trim() || !tiersValid;

  return (
    <section className="bb-section">
      <header className="bb-section__head">
        <div className="bb-section__title">
          <PlusCircle size={16} /> Campaign Builder
        </div>
      </header>

      <form className="bb-form" onSubmit={handleSubmit}>
        <label className="bb-field bb-field--wide">
          <span className="bb-field__label">Campaign name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CDCR low-value re-sweep — May 2026"
            required
          />
        </label>

        <label className="bb-field">
          <span className="bb-field__label">Department</span>
          <select value={deptCode} onChange={(e) => setDeptCode(e.target.value)}>
            <option value="">All departments</option>
            {departments.map((d) => {
              const code = d.department_code || d.dept_code || d.acronym;
              const label = d.acronym
                ? `${d.acronym} — ${d.common_name || d.official_name || ''}`
                : (d.common_name || d.official_name || code);
              return (
                <option key={d.department_id || code} value={code}>
                  {label}
                </option>
              );
            })}
          </select>
        </label>

        <label className="bb-field">
          <span className="bb-field__label">Min line total ($)</span>
          <input
            type="number"
            min="0"
            step="100"
            value={minLineTotal}
            onChange={(e) => setMinLineTotal(e.target.value)}
          />
        </label>

        <label className="bb-field">
          <span className="bb-field__label">Max items</span>
          <input
            type="number"
            min="1"
            step="100"
            value={maxItems}
            onChange={(e) => setMaxItems(e.target.value)}
          />
        </label>

        <fieldset className="bb-field bb-field--wide bb-tiers-pick">
          <legend className="bb-field__label">Tier cascade</legend>
          <div className="bb-tiers-pick__list">
            {TIER_OPTIONS.map((t) => (
              <label key={t.key} className={`bb-tier-pick ${tiers[t.key] ? 'is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={tiers[t.key]}
                  onChange={() => toggleTier(t.key)}
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
          {!tiersValid && (
            <div className="bb-field__hint bb-field__hint--warn">
              Pick at least one tier.
            </div>
          )}
        </fieldset>

        <div className="bb-form__actions">
          <button
            type="submit"
            className="bb-btn bb-btn--primary"
            disabled={disabled}
          >
            {create.isPending ? (
              <><Loader2 size={14} className="bb-spin" /> Creating…</>
            ) : (
              <><PlusCircle size={14} /> Create Campaign</>
            )}
          </button>
          {success && (
            <span className="bb-form__success">
              <CheckCircle2 size={14} /> Created — see history below.
            </span>
          )}
          {create.error && (
            <span className="bb-form__error">
              <AlertCircle size={14} /> {create.error.message}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

export default CampaignBuilder;
